import { useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type RestaurantTable,
  useCreateTable,
  useDeleteTable,
  useRotateTableQr,
  useTables,
} from "@/hooks/use-tables";
import { useActiveOrganization } from "@/hooks/use-organizations";

function buildQrUrl(orgSlug: string | null | undefined, qrToken: string) {
  if (!orgSlug || typeof window === "undefined") {
    return "";
  }
  return `${window.location.origin}/m/${orgSlug}?t=${qrToken}`;
}

function TableQrDialog({
  table,
  orgSlug,
  open,
  onOpenChange,
}: {
  table: RestaurantTable;
  orgSlug: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const url = buildQrUrl(orgSlug, table.qrToken);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{table.label}</DialogTitle>
          <DialogDescription>
            {t("tables.dialog.description", {
              defaultValue: "Escaneá este QR desde tu mesa para hacer pedidos.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-4">
          {url ? <QRCodeCanvas size={256} value={url} /> : null}
          <a
            className="break-all text-muted-foreground text-xs"
            href={url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {url}
          </a>
        </div>
        <DialogFooter>
          <Button onClick={() => window.print()} type="button" variant="outline">
            {t("tables.actions.print", { defaultValue: "Imprimir" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TablesPage() {
  const { t } = useTranslation();
  const { data: org } = useActiveOrganization();
  const { data, isLoading, error } = useTables();
  const createTable = useCreateTable();
  const deleteTable = useDeleteTable();
  const rotate = useRotateTableQr();
  const [newLabel, setNewLabel] = useState("");
  const [qrTable, setQrTable] = useState<RestaurantTable | null>(null);

  const handleCreate = async () => {
    if (!newLabel.trim()) {
      return;
    }
    try {
      await createTable.mutateAsync({ label: newLabel.trim() });
      setNewLabel("");
      toast.success(t("tables.notifications.created", { defaultValue: "Mesa creada" }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  const handleRotate = async (id: string) => {
    if (
      !window.confirm(
        t("tables.confirm.rotate", {
          defaultValue:
            "Al rotar el QR, el código impreso anterior dejará de funcionar. ¿Continuar?",
        }),
      )
    ) {
      return;
    }
    try {
      await rotate.mutateAsync(id);
      toast.success(t("tables.notifications.rotated", { defaultValue: "QR rotado" }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("tables.confirm.delete", { defaultValue: "¿Eliminar esta mesa?" }))) {
      return;
    }
    try {
      await deleteTable.mutateAsync(id);
      toast.success(t("tables.notifications.deleted", { defaultValue: "Mesa eliminada" }));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <h1 className="font-semibold text-2xl">{t("tables.title", { defaultValue: "Mesas" })}</h1>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("tables.create.title", { defaultValue: "Nueva mesa" })}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-2">
          <div className="flex-1">
            <Label htmlFor="newLabel">
              {t("tables.create.label", { defaultValue: "Etiqueta" })}
            </Label>
            <Input
              id="newLabel"
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Mesa 1"
              value={newLabel}
            />
          </div>
          <Button
            disabled={createTable.isPending || !newLabel.trim()}
            onClick={handleCreate}
            type="button"
          >
            {t("tables.actions.create", { defaultValue: "Crear" })}
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : error ? (
        <p className="text-destructive">{error.message}</p>
      ) : data && data.tables.length === 0 ? (
        <p className="rounded border border-border border-dashed p-6 text-center text-muted-foreground">
          {t("tables.empty", { defaultValue: "Sin mesas todavía" })}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data?.tables.map((table) => (
            <Card key={table.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>{table.label}</span>
                  {table.active ? null : (
                    <span className="text-muted-foreground text-xs">
                      {t("tables.inactive", { defaultValue: "Inactiva" })}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="font-mono text-muted-foreground text-xs">
                  /m/{org?.slug}?t={table.qrToken.slice(0, 12)}…
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setQrTable(table)} size="sm" type="button">
                    {t("tables.actions.viewQr", { defaultValue: "Ver QR" })}
                  </Button>
                  <Button
                    onClick={() => handleRotate(table.id)}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    {t("tables.actions.rotateQr", {
                      defaultValue: "Rotar QR",
                    })}
                  </Button>
                  <Button
                    onClick={() => handleDelete(table.id)}
                    size="sm"
                    type="button"
                    variant="destructive"
                  >
                    {t("tables.actions.delete", { defaultValue: "Eliminar" })}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {qrTable ? (
        <TableQrDialog
          onOpenChange={(open) => {
            if (!open) {
              setQrTable(null);
            }
          }}
          open={Boolean(qrTable)}
          orgSlug={org?.slug}
          table={qrTable}
        />
      ) : null}
    </div>
  );
}
